import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import { usePublicPrices } from "@/lib/usePublicPrices";
import AppShell from "@/components/AppShell";
import ProUpsellBanner from "@/components/ProUpsellBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fileToBase64, formatApiError, photoUrl } from "@/lib/utils-app";
import { UploadIcon as Upload, XIcon as X, DogIcon as Dog, PlusIcon as Plus } from "@phosphor-icons/react";
import PromoCodeInput from "@/components/PromoCodeInput";
import CompBanner from "@/components/CompBanner";
import DogPicker from "@/components/DogPicker";
import BreedPicker from "@/components/BreedPicker";
import InbreedingPreview from "@/components/InbreedingPreview";
import PhotoUploader from "@/components/PhotoUploader";
import DogVideoUploader from "@/components/DogVideoUploader";
import AddBreederDialog from "@/components/AddBreederDialog";
import DateOfBirthInput from "@/components/DateOfBirthInput";
import { isNativeApp, isIOS, webOrigin } from "@/lib/nativeBridge";
import IapConsumableButton from "@/components/IapConsumableButton";
import { toast } from "sonner";

const empty = {
  name: "",
  breed: "",
  sex: "male",
  dob: "",
  color: "",
  microchip: "",
  registration_number: "",
  sire_id: "",
  dam_id: "",
  breeder_id: "",
  current_owner: "",
  notes: "",
  photos: [],
};

export default function DogForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { prices } = usePublicPrices();
  const dogPrice = prices("dog_registration", "$25");
  const [form, setForm] = useState(empty);
  const [breeders, setBreeders] = useState([]);
  const [saving, setSaving] = useState(false);
  const [promo, setPromo] = useState(null);
  // Whether the user's active subscription covers a free dog registration.
  // We preflight this so the submit button says "Save" instead of
  // "Pay $25" for Pro / Kennel Elite (or any other plan with allowance
  // remaining). Backend already enforces this server-side via
  // `_plan_allows`; this is purely UI honesty.
  const [coversDog, setCoversDog] = useState(false);
  const [showAddBreeder, setShowAddBreeder] = useState(false);
  // Per-dog working videos (sourced from the dog doc after first load).
  // Only meaningful on the edit form (no dog id yet on create), but we
  // keep state here so the section animates in the moment the dog is saved.
  const [videos, setVideos] = useState([]);
  // Persona-aware copy: breeders get litter-sales framing on the video
  // teaser; everyone else (dog owners, buyers…) gets pet framing.
  const [viewerPersona, setViewerPersona] = useState(null);
  // Honor `?focus=photos` and `?focus=videos` deep-links from the
  // breeder-analytics dashboard Quick-win pills — scroll the user
  // straight to the relevant uploader once the form has data.
  const [searchParams] = useSearchParams();
  const photoSectionRef = useRef(null);
  const videoSectionRef = useRef(null);

  useEffect(() => {
    const focus = searchParams.get("focus");
    if (focus !== "photos" && focus !== "videos") return;
    // Wait a beat for the form to populate, then scroll smoothly.
    const t = setTimeout(() => {
      const target = focus === "videos" ? videoSectionRef.current : photoSectionRef.current;
      target?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 350);
    return () => clearTimeout(t);
  }, [searchParams, form.id]);

  const reloadBreeders = () => api.get("/breeders").then((r) => setBreeders(r.data));

  useEffect(() => {
    reloadBreeders();
    api.get("/me/persona", { silent: true })
      .then((r) => setViewerPersona(r.data?.persona || null))
      .catch(() => {});
    if (id) {
      api.get(`/dogs/${id}`).then((r) => {
        setForm({ ...empty, ...r.data });
        setVideos(Array.isArray(r.data?.videos) ? r.data.videos : []);
      });
    } else {
      // New registration: preselect the user's own kennel so the dog
      // automatically shows on their kennel page. Without this default,
      // most breeders skip the optional dropdown and later wonder why
      // their kennel page says "No dogs linked yet".
      api.get("/breeders/mine")
        .then((r) => {
          const mine = r.data;
          if (mine?.id) {
            setForm((f) => (f.breeder_id ? f : { ...f, breeder_id: mine.id }));
          }
        })
        .catch(() => {});
    }
    // Fast, single-call check — failure leaves the default "Pay" CTA,
    // which is the safer fallback (we'd rather charge once incorrectly
    // and trigger a no-op free path than promise free and then redirect
    // to Stripe).
    api.get("/billing/my-subscription")
      .then((r) => setCoversDog(!!r.data?.covers?.one_time_dog))
      .catch(() => setCoversDog(false));
  }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    // Before issuing the registration POST, capture how many dogs the
    // user already has. If it's zero, we'll set a flag in localStorage
    // so DogDetail.jsx shows the first-registration celebration. This
    // is a one-time UX delight moment, not a recurring banner.
    let isFirstDog = false;
    try {
      if (!id) {
        const r = await api.get("/dogs");
        isFirstDog = Array.isArray(r.data) && r.data.length === 0;
      }
    } catch { /* harmless — fall back to no celebration */ }
    try {
      const payload = { ...form };
      ["sire_id", "dam_id", "breeder_id"].forEach((k) => {
        if (payload[k] === "none") payload[k] = "";
      });
      if (id) {
        await api.put(`/dogs/${id}`, payload);
        toast.success("Dog updated");
        navigate(`/dogs/${id}`);
        return;
      }
      // New dog: route through billing checkout
      const { data } = await api.post("/billing/checkout", {
        package_id: "dog_registration",
        origin_url: webOrigin(),
        dog_payload: payload,
        ...(promo?.valid ? { promo_code: promo.code } : {}),
      });
      if (data.free && data.dog) {
        // Three reasons the registration was free:
        //   1. admin_override → admin role bypass (no real money was charged)
        //   2. promo applied → 100%-off discount code
        //   3. plan allowance → user has an active sub with available slots
        const reason = data.admin_override
          ? "free for admin"
          : data.promo
          ? "promo applied"
          : "plan allowance used";
        toast.success(`${data.dog.name} registered (${reason})`);
        track("dog_registered", { breed: data.dog?.breed });
        try { localStorage.removeItem("signup_seller_intent_breed"); } catch {}
        // Stash the "first dog" flag so DogDetail renders the
        // confetti + shareable kennel-card moment. Cleared as soon
        // as that page reads it.
        if (isFirstDog) {
          try {
            localStorage.setItem("celebrate_first_dog", data.dog.id);
          } catch {}
        }
        navigate(`/dogs/${data.dog.id}`);
        return;
      }
      if (data.url) {
        if (isFirstDog) {
          try {
            // Set a sentinel marker so the post-checkout return page
            // can detect "this was the user's first dog" even after
            // a Stripe redirect that resets in-memory state.
            localStorage.setItem("celebrate_first_dog_pending", "1");
          } catch {}
        }
        toast.message("Redirecting to secure checkout…");
        window.location.href = data.url;
        return;
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-2">
            {id ? "Edit" : "New dog"}
          </p>
          <h1 className="font-spectral text-4xl tracking-tight">
            {id ? form.name : "Register a dog"}
          </h1>
        </div>

        {/* Plan-coverage banner — surfaces clearly at the top of the
            form so users on Pro / Kennel Elite (or any plan with dogs
            allowance remaining) can see BEFORE filling out the form
            that they will not be charged. Mirrors the bottom-bar copy
            but at the top so it's the first thing they see. */}
        {!id && coversDog && (
          <div
            className="mb-6 rounded-xl border border-[#E8D9A6] bg-[#FBF7EC] px-4 py-3 flex items-center gap-3"
            data-testid="dog-form-plan-banner"
          >
            <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-gold text-ink text-[14px] font-bold">✓</span>
            <div className="text-[13px] text-ink leading-snug">
              <strong className="text-[#8A6A00]">You're covered by your plan</strong> —
              this dog registration is included. No charge. Just fill out the form and click <em>Register dog</em>.
            </div>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-8">
          <div className="bg-white border border-[#E5E5E5] rounded-xl p-6 md:p-8 shadow-[0_8px_30px_rgba(90,107,86,0.04)]">
            <h2 className="font-spectral text-2xl mb-6">Identity</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Name" required>
                <Input
                  required
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  data-testid="dog-name-input"
                  className="rounded-lg p-3"
                />
              </Field>
              <Field label="Breed" required>
                <BreedPicker
                  value={form.breed}
                  onChange={(v) => set("breed", v)}
                  placeholder="Pick a breed (purebred or exotic)…"
                  testId="dog-breed-picker"
                  required
                />
              </Field>
              <Field label="Sex" required>
                <Select value={form.sex} onValueChange={(v) => set("sex", v)}>
                  <SelectTrigger data-testid="dog-sex-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Date of birth">
                <DateOfBirthInput
                  id="dog-dob"
                  value={form.dob}
                  onChange={(v) => set("dob", v)}
                  max={new Date().toISOString().slice(0, 10)}
                  testId="dog-dob-input"
                  autoComplete="off"
                  ariaLabel="Dog date of birth"
                />
              </Field>
              <Field label="Color / markings">
                <Input
                  value={form.color}
                  onChange={(e) => set("color", e.target.value)}
                  data-testid="dog-color-input"
                  className="rounded-lg p-3"
                />
              </Field>
              <Field label="Microchip #">
                <Input
                  value={form.microchip}
                  onChange={(e) => set("microchip", e.target.value)}
                  data-testid="dog-microchip-input"
                  className="rounded-lg p-3"
                />
              </Field>
              <Field label="Registration #">
                {id ? (
                  <div
                    className="mt-1 rounded-lg p-3 bg-[#FAFAFA] border border-[#E5E5E5] font-mono text-sm text-ink"
                    data-testid="dog-regnum-display"
                  >
                    {form.registration_number || "—"}
                  </div>
                ) : (
                  <div
                    className="mt-1 rounded-lg p-3 bg-[#FAFAFA] border border-dashed border-[#E5E5E5] text-sm text-[#6B6B6B]"
                    data-testid="dog-regnum-placeholder"
                  >
                    Auto-assigned (e.g. AST000123) after registration
                  </div>
                )}
              </Field>
              <Field label="Current owner">
                <Input
                  value={form.current_owner}
                  onChange={(e) => set("current_owner", e.target.value)}
                  data-testid="dog-owner-input"
                  className="rounded-lg p-3"
                />
              </Field>
            </div>
          </div>

          <div className="bg-white border border-[#E5E5E5] rounded-xl p-6 md:p-8 shadow-[0_8px_30px_rgba(90,107,86,0.04)]">
            <h2 className="font-spectral text-2xl mb-6">Pedigree</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <Field label="Sire (father)">
                <DogPicker
                  value={form.sire_id || ""}
                  onChange={(idVal) => set("sire_id", idVal)}
                  sexFilter="male"
                  excludeIds={id ? [id] : []}
                  placeholder="Search sires by name or reg #"
                  testId="dog-sire-picker"
                />
              </Field>
              <Field label="Dam (mother)">
                <DogPicker
                  value={form.dam_id || ""}
                  onChange={(idVal) => set("dam_id", idVal)}
                  sexFilter="female"
                  excludeIds={id ? [id] : []}
                  placeholder="Search dams by name or reg #"
                  testId="dog-dam-picker"
                />
              </Field>
              <Field label="Breeder">
                <Select
                  value={form.breeder_id || "none"}
                  onValueChange={(v) => set("breeder_id", v)}
                >
                  <SelectTrigger data-testid="dog-breeder-select">
                    <SelectValue placeholder="Select breeder" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {breeders.map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.kennel_name || b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <button
                  type="button"
                  onClick={() => setShowAddBreeder(true)}
                  className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.12em] text-ink hover:text-crimson"
                  data-testid="dog-add-breeder-btn"
                >
                  <Plus size={11} weight="bold" /> Add breeder not listed
                </button>
              </Field>
            </div>
            <InbreedingPreview sireId={form.sire_id} damId={form.dam_id} />
          </div>

          <div
            ref={photoSectionRef}
            className="bg-white border border-[#E5E5E5] rounded-xl p-6 md:p-8 shadow-[0_8px_30px_rgba(90,107,86,0.04)]"
            data-testid="dog-photo-section"
          >
            <h2 className="font-spectral text-2xl mb-6">Photos</h2>
            <PhotoUploader
              photos={form.photos}
              onChange={(next) => setForm((f) => ({ ...f, photos: next }))}
            />
          </div>

          {/* Videos — only available once the dog has been saved at least once.
              On the create form we show a teaser banner so breeders know it's
              coming after first save, but the actual uploader needs an id. */}
          {id ? (
            <div
              ref={videoSectionRef}
              className="bg-white border border-[#E5E5E5] rounded-xl p-6 md:p-8 shadow-[0_8px_30px_rgba(90,107,86,0.04)]"
              data-testid="dog-video-section"
            >
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-spectral text-2xl">Working videos</h2>
                <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#8A6A00] bg-[#FBF1D2] border border-[#E8D9A6] px-2 py-0.5 rounded-full">
                  {videos.length}/3
                </span>
              </div>
              <DogVideoUploader
                dogId={id}
                videos={videos}
                onChange={setVideos}
              />
            </div>
          ) : (
            <div
              className="bg-[#FBF7EC] border border-[#E8D9A6] rounded-xl p-5 text-[13px] text-[#3F3F3F]"
              data-testid="dog-video-section-teaser"
            >
              <div className="flex items-start gap-2.5">
                <span className="text-[#8A6A00] text-base leading-none mt-0.5">▶</span>
                {viewerPersona && viewerPersona !== "breeder" ? (
                  <div>
                    <p className="font-bold text-ink mb-0.5">
                      You&apos;ll be able to add videos of your dog right after you save.
                    </p>
                    <p className="leading-snug">
                      Tricks, playtime, training, zoomies — up to 3 clips per dog,
                      uploaded directly OR a YouTube/Vimeo link.
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="font-bold text-ink mb-0.5">
                      You&apos;ll be able to add working videos (bitework, hunt, agility, obedience) right after you save.
                    </p>
                    <p className="leading-snug">
                      Buyers consistently say videos are the #1 thing that closes a litter. Up to 3 clips per dog,
                      uploaded directly OR a YouTube/Vimeo link.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="bg-white border border-[#E5E5E5] rounded-xl p-6 md:p-8 shadow-[0_8px_30px_rgba(90,107,86,0.04)]">
            <h2 className="font-spectral text-2xl mb-4">Notes</h2>
            <Textarea
              rows={4}
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              data-testid="dog-notes-input"
              className="rounded-lg"
            />
          </div>

          {/* Upsell — only show when the user is on the paid path and could
              save money by upgrading. Keeps the one-off pay button visible
              (no friction increase) but routes intentional shoppers toward
              the better-value subscription, which prevents the bulk of
              abandoned-cart attempts at the source. */}
          {!id && !coversDog && (
            <ProUpsellBanner context="dog-form" />
          )}
          {!id && !coversDog && !isNativeApp() && (
            <div
              className="rounded-xl border border-[#C9A227]/40 bg-[#FFF8E5] p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4"
              data-testid="dog-form-upsell"
            >
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-bold uppercase tracking-[0.15em] text-[#8A6A00] mb-1">
                  Registering more than one dog?
                </p>
                <p className="text-[13px] text-[#0E0E0E]/85 leading-snug">
                  This dog costs <strong>$25 one-off</strong>. The{" "}
                  <strong>Basic plan</strong> ($19/mo, 10 dogs/yr) pays for itself with{" "}
                  <strong>just a couple dogs a year</strong>. <strong>Pro</strong> ($44/mo)
                  unlocks unlimited dogs &amp; litters, free pedigree certificates,
                  Dog Log, and a 9% platform fee on every sale.
                </p>
              </div>
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white text-[12px] font-bold uppercase tracking-[0.12em] px-4 py-2 shrink-0 transition-colors"
                data-testid="dog-form-upgrade-link"
              >
                See plans →
              </Link>
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white border border-[#E5E5E5] rounded-xl p-4">
            {!id && coversDog ? (
              <p className="flex-1 text-[12px] text-[#0E0E0E]/75 leading-snug" data-testid="dog-form-plan-included">
                <strong className="text-[#8A6A00]">Included in your plan</strong> —
                no payment required. Click <em>Register dog</em> to save it to your kennel.
              </p>
            ) : !id ? (
              <div className="flex-1 max-w-md">
                {!isNativeApp() && (
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-2">
                  Promo code (optional)
                </p>
                )}
                <CompBanner className="mb-3" />
                <PromoCodeInput
                  kind="one_time_dog"
                  amount={25}
                  applied={promo}
                  onApplied={(p) => {
                    setPromo(p);
                    toast.success(`${p.code} applied — final $${p.final.toFixed(2)}`);
                  }}
                  onCleared={() => setPromo(null)}
                />
              </div>
            ) : null}
            <div className="flex flex-col items-end gap-1 sm:ml-auto">
              <div className="flex flex-wrap justify-end gap-3">
              <Link to={id ? `/dogs/${id}` : "/dogs"}>
                <Button type="button" variant="outline" className="rounded-full border-[#E5E5E5]" data-testid="dog-cancel-btn">
                  Cancel
                </Button>
              </Link>
              {!(isIOS() && !id && !coversDog && !(promo?.valid && promo.final === 0)) && (
              <Button
                type="submit"
                disabled={saving}
                className="bg-[#0E0E0E] hover:bg-[#C1272D] text-white rounded-full px-8"
                data-testid="dog-submit-btn"
              >
                {saving
                  ? "Processing…"
                  : id
                  ? "Save changes"
                  : coversDog
                  ? "Register dog (included in your plan)"
                  : promo?.valid && promo.final === 0
                  ? "Register dog (promo applied)"
                  : promo?.valid
                  ? (isNativeApp() ? `Continue to checkout in browser — $${promo.final.toFixed(2)}` : `Pay $${promo.final.toFixed(2)} & register`)
                  : (isNativeApp() ? `Continue to checkout in browser — ${dogPrice}` : `Pay ${dogPrice} & register`)}
              </Button>
              )}
              </div>
              {isIOS() && !id && !coversDog && !(promo?.valid && promo.final === 0) && (
                <IapConsumableButton
                  creditKey="dog_registration"
                  label="Buy dog registration"
                  noticeLabel="One-time dog registration"
                  onGranted={() => onSubmit({ preventDefault: () => {} })}
                  className="mt-1"
                />
              )}
              {isNativeApp() && !isIOS() && !id && !coversDog && !(promo?.valid && promo.final === 0) && (
                <p className="text-[11px] text-[#9CA3AF] mt-1 text-right" data-testid="dog-form-browser-hint">
                  Checkout opens securely in your browser
                </p>
              )}
            </div>
          </div>
        </form>
      </div>

      <AddBreederDialog
        open={showAddBreeder}
        onClose={() => setShowAddBreeder(false)}
        existingBreeders={breeders}
        onPickedExisting={(b) => set("breeder_id", b.id)}
        onCreated={async (b) => {
          await reloadBreeders();
          set("breeder_id", b.id);
        }}
      />
    </AppShell>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
        {label} {required && <span className="text-crimson">*</span>}
      </Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
